import { useState } from "react";
import { useLanguageContext } from "../../../context/LanguageContext";
import {
  faCheck,
  faPen,
  faRobot,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { StyledInput } from "../../../styles/InputStyles";
import { useAuthContext } from "../../../context/AuthContext";
import FileHelper from "../../../helpers/FileHelper";
import useEvaApi, { EvaApiCall } from "../../../hooks/useEvaApi";
import DataTableConfig from "../main/config/dataTable/DataTableConfig";
import useBasicColumnConfig from "../main/config/column/default/BasicColumnConfig";
import RowActionConfig from "../main/config/rowAction/RowActionConfig";
import DataTable, { useDataTable } from "../main/DataTable";
import useBooleanColumnConfig from "../main/config/column/default/BooleanColumnConfig";
import useExportHeaderActionConfig from "../main/config/headerAction/default/ExportHeaderActionConfig";
import useImportHeaderActionConfig from "../main/config/headerAction/default/ImportHeaderActionConfig";
import HeaderActionConfig from "../main/config/headerAction/HeaderActionConfig";
import { useLoadContext } from "../../../context/LoadContext";

function TranslationsDataTable({ languageData }) {
  const callEvaApi = useEvaApi();
  const authContext = useAuthContext();
  const loadContext = useLoadContext();
  const { translate, fetchTranslations, language } = useLanguageContext();
  const [editRow, setEditRow] = useState(null);

  //Create the datatable config
  const config = new DataTableConfig("translations");

  //Set the datatable header
  config.setHeader(translate("eva.main.entities.translations"));

  //Set the datatable fetch info
  config.setFetchRequest(`translations/${languageData.id}`);
  config.setFetchParams({
    where:
      authContext.auth.user.company.type === "superadmin"
        ? []
        : [
            {
              column: "key",
              values: [{ value: "eva.frontoffice" }],
            },
          ],
  });

  //Set the datatable order by
  config.setOrderBy("key");
  config.setOrderDirection("asc");

  //set the datatable columns
  config.setColumns([
    useBasicColumnConfig("key").setLockActive(true),
    useBasicColumnConfig("value")
      .setLockActive(true)
      .setData(getValueColumnData),
    useBooleanColumnConfig("mutated").setActive(false),
  ]);

  //Set the datatable row actions
  config.setRowActions([
    new RowActionConfig()
      .setIcon(faPen)
      .setCallback(handleEdit)
      .setHidden((row) => row.id === editRow?.id)
      .setTitle((row) =>
        translate("eva.main.entities.states.edit", [
          {
            key: "entities",
            value: translate("eva.main.entities.translation"),
          },
        ])
      )
      .setDisabled((row) =>
        loadContext.isLoading([
          `translation_ai_translate_${row.id}`,
          "language_ai_translate",
        ])
      ),

    new RowActionConfig()
      .setIcon(faXmark)
      .setCallback(handleCancel)
      .setHidden((row) => row.id !== editRow?.id)
      .setTitle(translate("eva.main.general.cancel"))
      .setDisabled((row) => loadContext.isLoading(`language_ai_translate`)),

    new RowActionConfig()
      .setIcon(faCheck)
      .setCallback(handleConfirm)
      .setHidden((row) => row.id !== editRow?.id)
      .setTitle(translate("eva.main.general.save"))
      .setDisabled((row) =>
        loadContext.isLoading([
          `${config.getDataTableKey()}_update_${row.id}`,
          "language_ai_translate",
        ])
      ),

    new RowActionConfig()
      .setIcon(faRobot)
      .setCallback(handleAITranslateTranslation)
      .setHidden(
        (row) =>
          row.id === editRow?.id ||
          authContext.auth.user.company.type !== "superadmin"
      )
      .setLoadingGroups((row) => `translation_ai_translate_${row.id}`)
      .setTitle("AI")
      .setDisabled((row) => loadContext.isLoading(["language_ai_translate"])),
  ]);

  //Set the datatable header actions
  config.setHeaderActions([
    new HeaderActionConfig()
      .setIcon(faRobot)
      .setLabel("AI")
      .setCallback(handleAITranslateLanguage)
      .setLoadingGroups("language_ai_translate")
      .setHidden(authContext.auth.user.company.type !== "superadmin"),

    useExportHeaderActionConfig("translations", handleExport).setDisabled(() =>
      loadContext.isLoading("language_ai_translate")
    ),
    useImportHeaderActionConfig("translations", handleImport).setDisabled(() =>
      loadContext.isLoading("language_ai_translate")
    ),
  ]);

  //Set the overall loading groups for the datatable row actions
  config.setRowActionsLoadingGroups(
    (row) => `${config.getDataTableKey()}_update_${row.id}`
  );

  //Create the datatable object
  const dataTable = useDataTable(config);

  /**
   * This method will return the visual representation of the value column
   * @param {object} row
   * @returns {string|any}
   */
  function getValueColumnData(row) {
    if (row.id !== editRow?.id) {
      return row.value;
    } else {
      return (
        <StyledInput
          as={"textarea"}
          value={editRow.value}
          width={"100%"}
          onChange={(e) => setEditRow({ ...editRow, value: e.target.value })}
        />
      );
    }
  }

  /**
   * This method will start the edit state of the given row
   * @param {object} row
   */
  function handleEdit(row) {
    setEditRow(row);
  }

  /**
   * This method will cancel the update of the given row
   * @param {object} row
   */
  function handleCancel(row) {
    setEditRow(null);
  }

  /**
   * This method will send a request to the API for updating the given row
   * @param {object} row
   */
  async function handleConfirm(row) {
    if (editRow.value === row.value) {
      setEditRow(null);
      return;
    }

    await callEvaApi(
      new EvaApiCall(`translations/${languageData.id}/${row.id}`)
        .setLoadingGroup(`${config.getDataTableKey()}_update_${row.id}`)
        .setMethod("PUT")
        .setData({ value: editRow.value })
        .setOnSuccess((response) => {
          row.value = response.data.data.value;
          setEditRow(null);

          dataTable.fetch();

          //Update the app translations if the app language has been updated (main translations only)
          if (
            parseInt(languageData.id) === parseInt(language.id) &&
            row.key.includes("eva.main")
          ) {
            fetchTranslations();
          }
        })
    );
  }

  /**
   * This method will try and add a new record
   */
  async function handleExport() {
    await callEvaApi(
      new EvaApiCall(`translations/${languageData.id}/export`)
        .setLoadingGroup("translations_export")
        .setDownloadAs(`${languageData.code}_translations`)
        .setParams({
          ...dataTable.filters,
          frontoffice_only: authContext.auth.user.company.type !== "superadmin",
        })
    );
  }

  /**
   * This method will try and add a new record
   */
  async function handleImport() {
    const selectedFile = await FileHelper.select(".xlsx");
    if (!selectedFile) {
      return;
    }

    await callEvaApi(
      new EvaApiCall(`translations/${languageData.id}/import`)
        .setMethod("POST")
        .setLoadingGroup("translations_import")
        .setData({ file: selectedFile })
        .setOnSuccess(() => {
          dataTable.fetch();
        })
    );
  }

  /**
   * This method will call the AI translate endpoint for the current language
   */
  async function handleAITranslateLanguage() {
    await callEvaApi(
      new EvaApiCall(`ai/languages/${languageData.id}/translate`)
        .setMethod("POST")
        .setLoadingGroup("language_ai_translate")
        .setOnSuccess(() => {
          dataTable.fetch();
        })
    );
  }

  /**
   * This method will call the AI translate endpoint for the given translation row
   * @param {object} row
   */
  async function handleAITranslateTranslation(row) {
    await callEvaApi(
      new EvaApiCall(`ai/translations/${row.id}/translate`)
        .setMethod("POST")
        .setLoadingGroup(`translation_ai_translate_${row.id}`)
        .setOnSuccess(() => {
          dataTable.fetch();
        })
    );
  }

  return <DataTable controller={dataTable} />;
}

export default TranslationsDataTable;
